import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import { LinkContainer } from "react-router-bootstrap";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Stack from "react-bootstrap/esm/Stack";
import Alert from "react-bootstrap/esm/Alert";
import Spinner from "react-bootstrap/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup, faThumbTack } from "@fortawesome/free-solid-svg-icons";

import { AppLayout } from "../components/appLayout";
import useApiClient from "../hooks/useApiClient";
import { CategoryWithCountsViewModel, CreateBookmarkBindingModel, CreateBookmarksBindingModel } from "../apiClient";
import { CategorySelect, CountType } from "../components/inputs/categorySelect";
import { getErrorMessage } from "../helpers/errorHelper";
import { getAddBookmarkBookmarkletHref } from "../components/forms/bookmarkAddEditForm";

export const BookmarkBulkAdd = () => {
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialUrl = searchParams.get('url') ?? '';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | Array<{ url: string; reason: string }> | null>(null);
    const [categories, setCategories] = useState<CategoryWithCountsViewModel[]>([]);

    useEffect(() => {
        if (!apiClient) return;
        (async () => {
            const data = await apiClient.categoriesAll();
            setCategories(data);
            setLoading(false);
        })();
    }, [apiClient]);

    const formik = useFormik({
        initialValues: {
            categoryName: '',
            bookmarkUrls: initialUrl
        },
        onSubmit: async (values, helpers) => {
            setError(null);
            if (!apiClient) return;
            try {
                const lines: string[] = Array.from(new Set(values.bookmarkUrls.split('\n').map(x => x.trim()).filter(x => x)));
                const payload = new CreateBookmarksBindingModel({
                    bookmarks: lines.map(url => new CreateBookmarkBindingModel({ url, categoryName: values.categoryName || null }))
                });
                
                const result = await apiClient.bookmarksPOST(payload);
                const failedList = result?.failedBookmarks ?? [];
                
                if (failedList.length > 0) {
                    setError(failedList.map(fb => ({
                        url: fb.url ?? '',
                        reason: fb.reason ?? 'Unknown reason'
                    })));
                    
                    const createdUrls = result.addedBookmarks?.map(b => b.url) ?? [];
                    const notCreatedUrls = lines.filter(url => !createdUrls.includes(url));
                    await helpers.setFieldValue('bookmarkUrls', notCreatedUrls.join('\n'));
                    return;
                }
                
                navigate('/bookmarks');
            } catch (err) {
                console.error(err);
                setError(getErrorMessage(err));
            } finally {
                helpers.setSubmitting(false);
            }
        }
    });

    const urlCount = formik.values.bookmarkUrls
        .split('\n')
        .map(x => x.trim())
        .filter(Boolean).length;

    return (
        <AppLayout loading={loading}>
            <Container className="py-2">
                <Breadcrumb>
                    <LinkContainer to="/bookmarks">
                        <Breadcrumb.Item>Bookmarks</Breadcrumb.Item>
                    </LinkContainer>
                    <Breadcrumb.Item active>Bulk add bookmarks</Breadcrumb.Item>
                </Breadcrumb>

                <div className="mb-3">
                    <h1 className="mb-1">Bulk add bookmarks</h1>
                    <p className="text-body-secondary mb-0">
                        Paste multiple URLs (one per line) to add them all at once.
                    </p>
                </div>

                <Card>
                    <Card.Body className="p-4">
                        <Form onSubmit={formik.handleSubmit}>
                            <fieldset disabled={formik.isSubmitting}>
                                <Stack gap={3}>
                                    {error && (
                                        <Alert variant="danger" className="shadow-sm border-start border-4 border-danger">
                                            <h5 className="alert-heading fw-bold mb-2">Failed to add some bookmarks</h5>
                                            {Array.isArray(error) ? (
                                                <ul className="mb-0 ps-3">
                                                    {error.map((err, index) => (
                                                        <li key={index} className="mb-1">
                                                            <strong className="text-break">{err.url}</strong>: <span className="text-muted">{err.reason}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <div style={{ whiteSpace: 'pre-wrap' }}>{error}</div>
                                            )}
                                        </Alert>
                                    )}

                                    <Form.Group controlId="bulk-bookmark-urls">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <Form.Label className="mb-0">Bookmark URLs (one per line)</Form.Label>
                                            {urlCount > 0 && (
                                                <Badge bg="secondary-subtle" className="text-secondary-emphasis">
                                                    {urlCount} {urlCount === 1 ? 'URL' : 'URLs'}
                                                </Badge>
                                            )}
                                        </div>
                                        <Form.Control
                                            as="textarea"
                                            rows={10}
                                            name="bookmarkUrls"
                                            placeholder={"https://www.youtube.com/watch?v=...\nhttps://vimeo.com/..."}
                                            value={formik.values.bookmarkUrls}
                                            onChange={formik.handleChange}
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group>
                                        <CategorySelect
                                            categories={categories}
                                            name="categoryName"
                                            value={formik.values.categoryName}
                                            onChange={val => formik.setFieldValue('categoryName', val)}
                                            countType={CountType.Bookmarks}
                                        />
                                    </Form.Group>

                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 pt-2">
                                        <Stack direction="horizontal" gap={2}>
                                            <Button variant="primary" type="submit" disabled={!formik.isValid || formik.isSubmitting}>
                                                {formik.isSubmitting ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                        Adding...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                                                        Add bookmarks
                                                    </>
                                                )}
                                            </Button>
                                            <Button variant="secondary" onClick={() => navigate('/bookmarks')}>
                                                Cancel
                                            </Button>
                                        </Stack>

                                        <div className="d-flex align-items-center gap-2 small text-body-secondary">
                                            <span>Drag to bookmarks bar:</span>
                                            <a
                                                ref={(el) => {
                                                    if (el) el.setAttribute('href', getAddBookmarkBookmarkletHref());
                                                }}
                                                href="#bookmarklet"
                                                onClick={(e) => e.preventDefault()}
                                                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                                                title="Drag this link to your browser's bookmarks bar to quickly bookmark any page"
                                            >
                                                <FontAwesomeIcon icon={faThumbTack} size="xs" />
                                                <span>+ Add Bookmark</span>
                                            </a>
                                        </div>
                                    </div>
                                </Stack>
                            </fieldset>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        </AppLayout>
    );
};